// Check pages commands (05 §6.3; names and payloads exactly 01 §6.2, 90 #42).
//
// Constructors build 01's command objects. Optional fields marked `?` in 01
// are omitted when not given; required ones are always present.
// applyCommand(ws, cmd) is the optimistic local mirror of 01's "Effect"
// column. It is pure: it returns {ws, prev} with a new workspace and the
// untouched input (the rollback snapshot), or throws CommandRejected with 01's
// validation `reason` when the server would refuse the command.
import { v4 as uuidv4 } from 'uuid';
import { CommandType } from '../../../Exams/shared/enums';
import { RESPONSE, finalize, rosterEntry, scriptLabelFor } from './model';

export class CommandRejected extends Error {
  constructor(reason, command) {
    super(reason);
    this.name = 'CommandRejected';
    this.reason = reason;
    this.command = command;
  }
}

const withOptional = (base, optional) => {
  const out = { ...base };
  Object.entries(optional).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  });
  return out;
};

// ------------------------------------------------------------ constructors

export const movePage = (pageId, toScriptId = null, position = null) => ({
  type: CommandType.MOVE_PAGE,
  pageId,
  toScriptId: toScriptId ?? null,
  position: position ?? null,
});

export const reorderPages = (scriptId, pageIds) => ({
  type: CommandType.REORDER_PAGES,
  scriptId,
  pageIds: [...pageIds],
});

export const newScript = ({
  scriptId = uuidv4(),
  pageIds,
  name,
  studentId,
  rosterId,
}) =>
  withOptional(
    { type: CommandType.NEW_SCRIPT, scriptId, pageIds: [...pageIds] },
    { name, studentId, rosterId }
  );

export const splitScript = (scriptId, fromPageId, newScriptId = uuidv4()) => ({
  type: CommandType.SPLIT_SCRIPT,
  scriptId,
  fromPageId,
  newScriptId,
});

export const setIdentity = ({
  scriptId,
  name,
  studentId,
  rosterId,
  isNewStudent = false,
}) =>
  withOptional(
    {
      type: CommandType.SET_IDENTITY,
      scriptId,
      name: name ?? '',
      isNewStudent: Boolean(isNewStudent),
    },
    { studentId, rosterId }
  );

export const confirmMatch = (scriptId) => ({
  type: CommandType.CONFIRM_MATCH,
  scriptId,
});

export const setPageKind = (pageId, kind) => ({
  type: CommandType.SET_PAGE_KIND,
  pageId,
  kind,
});

export const setQuestionStart = ({
  scriptId,
  questionId,
  pageId,
  lineIndex,
}) => ({
  type: CommandType.SET_QUESTION_START,
  scriptId,
  questionId,
  pageId,
  lineIndex,
});

export const clearQuestionStart = (scriptId, questionId) => ({
  type: CommandType.CLEAR_QUESTION_START,
  scriptId,
  questionId,
});

export const ACCEPT_MIN_CONFIDENCE = 0.9;

export const acceptSuggestions = ({
  minConfidence = ACCEPT_MIN_CONFIDENCE,
  pageIds = null,
} = {}) => ({
  type: CommandType.ACCEPT_SUGGESTIONS,
  minConfidence,
  pageIds: pageIds ? [...pageIds] : null,
});

export const MAX_PAGE_TEXT = 20000;

export const editPageText = (pageId, text) => ({
  type: CommandType.EDIT_PAGE_TEXT,
  pageId,
  text,
});

export const revertPageText = (pageId) => ({
  type: CommandType.REVERT_PAGE_TEXT,
  pageId,
});

export const ROTATIONS = Object.freeze([0, 90, 180, 270]);

export const rotatePage = (pageId, rotation) => ({
  type: CommandType.ROTATE_PAGE,
  pageId,
  rotation,
});

export const setMode = (mode) => ({ type: CommandType.SET_MODE, mode });

export const assignPagesToStudent = (pageIds, rosterId) => ({
  type: CommandType.ASSIGN_PAGES_TO_STUDENT,
  pageIds: [...pageIds],
  rosterId,
});

// ------------------------------------------------------------ apply helpers

const reject = (reason, command) => {
  throw new CommandRejected(reason, command);
};

/** A shallow copy-on-write draft of the workspace for one command. */
const draft = (ws) => ({
  ...ws,
  pages: { ...ws.pages },
  scripts: { ...ws.scripts },
});

const pageOf = (ws, pageId, command) =>
  ws.pages[pageId] || reject('PAGE_NOT_FOUND', command);

const scriptOf = (ws, scriptId, command) =>
  ws.scripts[scriptId] || reject('SCRIPT_NOT_FOUND', command);

/** Set a script's page list and mirror scriptId/position on those pages. */
const setScriptPages = (ws, scriptId, pageIds) => {
  ws.scripts[scriptId] = {
    ...ws.scripts[scriptId],
    pageIds,
    pageCount: pageIds.length,
  };
  pageIds.forEach((id, index) => {
    const page = ws.pages[id];
    if (page && (page.scriptId !== scriptId || page.position !== index)) {
      ws.pages[id] = { ...page, scriptId, position: index };
    }
  });
};

/** Take a page out of its script (if any). Emptied scripts disappear. */
const detachPage = (ws, pageId) => {
  const page = ws.pages[pageId];
  if (!page?.scriptId) return;
  const script = ws.scripts[page.scriptId];
  ws.pages[pageId] = { ...page, scriptId: null, position: -1 };
  if (!script) return;
  const remaining = script.pageIds.filter((id) => id !== pageId);
  if (remaining.length === 0) {
    delete ws.scripts[script.scriptId];
  } else {
    setScriptPages(ws, script.scriptId, remaining);
  }
};

const nextScriptNo = (ws) =>
  Object.values(ws.scripts).reduce(
    (max, script) => Math.max(max, Number(script.scriptNo) || 0),
    0
  ) + 1;

const createScript = (ws, scriptId, fields = {}) => {
  const no = nextScriptNo(ws);
  ws.scripts[scriptId] = {
    scriptId,
    scriptNo: no,
    label: scriptLabelFor(no),
    railKind: fields.name || fields.studentId ? 'MATCHED' : 'NEW',
    name: fields.name ?? null,
    studentId: fields.studentId ?? null,
    rosterId: fields.rosterId ?? null,
    source: fields.rosterId
      ? 'ROSTER'
      : fields.name || fields.studentId
      ? 'MANUAL'
      : null,
    confidence: null,
    matchNote: fields.name || fields.studentId ? 'ADDED_BY_YOU' : null,
    isNewStudent: !fields.rosterId && Boolean(fields.name || fields.studentId),
    needsCheck: false,
    pageIds: [],
    pageCount: 0,
    typedWords: 0,
    credits: 1,
    questionStarts: [],
    transcriptionEdited: false,
  };
};

const requireResponse = (page, command) => {
  if (page.kind !== RESPONSE) reject('PAGE_SET_ASIDE', command);
};

/** Insert pageId into a script at position (null = end). */
const insertPage = (ws, pageId, scriptId, position, command) => {
  const script = scriptOf(ws, scriptId, command);
  const list = script.pageIds.filter((id) => id !== pageId);
  const at =
    position === null || position === undefined ? list.length : position;
  if (!Number.isInteger(at) || at < 0 || at > list.length) {
    reject('POSITION_OUT_OF_RANGE', command);
  }
  list.splice(at, 0, pageId);
  setScriptPages(ws, scriptId, list);
};

// ----------------------------------------------------------------- appliers

const APPLY = {
  [CommandType.MOVE_PAGE]: (ws, cmd) => {
    const page = pageOf(ws, cmd.pageId, cmd);
    requireResponse(page, cmd);
    if (cmd.toScriptId) scriptOf(ws, cmd.toScriptId, cmd);
    if (cmd.toScriptId && page.scriptId === cmd.toScriptId) {
      insertPage(ws, cmd.pageId, cmd.toScriptId, cmd.position, cmd);
      return;
    }
    // Validate the position against the target before detaching.
    if (cmd.toScriptId) {
      const target = ws.scripts[cmd.toScriptId];
      const max = target.pageIds.length;
      if (
        cmd.position !== null &&
        cmd.position !== undefined &&
        (!Number.isInteger(cmd.position) ||
          cmd.position < 0 ||
          cmd.position > max)
      ) {
        reject('POSITION_OUT_OF_RANGE', cmd);
      }
    }
    detachPage(ws, cmd.pageId);
    if (cmd.toScriptId) {
      insertPage(ws, cmd.pageId, cmd.toScriptId, cmd.position, cmd);
    }
  },

  [CommandType.REORDER_PAGES]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    const current = [...script.pageIds].sort();
    const next = [...(cmd.pageIds || [])].sort();
    if (
      current.length !== next.length ||
      current.some((id, index) => id !== next[index])
    ) {
      reject('NOT_A_PERMUTATION', cmd);
    }
    setScriptPages(ws, cmd.scriptId, [...cmd.pageIds]);
  },

  [CommandType.NEW_SCRIPT]: (ws, cmd) => {
    if (ws.scripts[cmd.scriptId]) reject('SCRIPT_ID_TAKEN', cmd);
    const ids = cmd.pageIds || [];
    if (ids.length === 0) reject('EMPTY_SCRIPT', cmd);
    ids.forEach((id) => requireResponse(pageOf(ws, id, cmd), cmd));
    ids.forEach((id) => detachPage(ws, id));
    createScript(ws, cmd.scriptId, cmd);
    setScriptPages(ws, cmd.scriptId, [...ids]);
  },

  [CommandType.SPLIT_SCRIPT]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    const index = script.pageIds.indexOf(cmd.fromPageId);
    if (index < 0) reject('PAGE_NOT_FOUND', cmd);
    if (index === 0) reject('CANNOT_SPLIT_FIRST_PAGE', cmd);
    if (ws.scripts[cmd.newScriptId]) reject('SCRIPT_ID_TAKEN', cmd);
    const head = script.pageIds.slice(0, index);
    const tail = script.pageIds.slice(index);
    setScriptPages(ws, cmd.scriptId, head);
    createScript(ws, cmd.newScriptId);
    setScriptPages(ws, cmd.newScriptId, tail);
  },

  [CommandType.SET_IDENTITY]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    const name = String(cmd.name ?? '').trim();
    const studentId = cmd.studentId ? String(cmd.studentId).trim() : null;
    if (!name && !studentId && !cmd.rosterId) {
      // Bulk Upload's "Clear its name" (06 §4; BE clearIdentity): no name, ID
      // or roster entry leaves the script unnamed. An exam script always
      // needs a name or ID (01 §6).
      if (ws.ownerKind !== 'BULK_UPLOAD') reject('NAME_OR_ID_REQUIRED', cmd);
      ws.scripts[cmd.scriptId] = {
        ...script,
        name: null,
        studentId: null,
        rosterId: null,
        source: null,
        isNewStudent: false,
        needsCheck: false,
        railKind: 'UNNAMED',
        matchNote: null,
      };
      return;
    }
    if (!name && !studentId) reject('NAME_OR_ID_REQUIRED', cmd);
    // Mirrors the server (01 §6): a roster-only job commits class members
    // only, so a name or ID needs a roster entry; the blank clear is above.
    if (ws.rosterOnly && !cmd.rosterId) reject('NOT_A_CLASS_MEMBER', cmd);
    if (cmd.rosterId && ws.roster?.length && !rosterEntry(ws, cmd.rosterId)) {
      reject('ROSTER_ENTRY_NOT_FOUND', cmd);
    }
    ws.scripts[cmd.scriptId] = {
      ...script,
      name: name || null,
      studentId,
      rosterId: cmd.rosterId ?? null,
      source: cmd.rosterId ? 'ROSTER' : 'MANUAL',
      isNewStudent: Boolean(cmd.isNewStudent),
      needsCheck: false,
      railKind: 'MATCHED',
      matchNote: cmd.isNewStudent ? 'ADDED_BY_YOU' : 'ASSIGNED_BY_YOU',
      ...(ws.rosterOnly ? { unroutable: false } : {}),
    };
  },

  [CommandType.CONFIRM_MATCH]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    if (!script.needsCheck) reject('NOTHING_TO_CONFIRM', cmd);
    ws.scripts[cmd.scriptId] = {
      ...script,
      source: 'CONFIRMED',
      needsCheck: false,
      railKind: 'MATCHED',
      matchNote: 'CONFIRMED_BY_YOU',
    };
  },

  [CommandType.SET_PAGE_KIND]: (ws, cmd) => {
    const page = pageOf(ws, cmd.pageId, cmd);
    const isResponse = page.kind === RESPONSE;
    if (cmd.kind === RESPONSE && isResponse) reject('ALREADY_THAT_KIND', cmd);
    if (cmd.kind !== RESPONSE && !isResponse) reject('ALREADY_THAT_KIND', cmd);
    if (cmd.kind !== RESPONSE) {
      const from = page.scriptId
        ? { scriptId: page.scriptId, position: page.position }
        : null;
      detachPage(ws, cmd.pageId);
      ws.pages[cmd.pageId] = {
        ...ws.pages[cmd.pageId],
        kind: cmd.kind,
        setAsideFrom: from,
      };
      return;
    }
    const from = page.setAsideFrom;
    ws.pages[cmd.pageId] = { ...page, kind: RESPONSE, setAsideFrom: null };
    const target = from?.scriptId ? ws.scripts[from.scriptId] : null;
    if (target) {
      const at = Math.min(
        Math.max(0, from.position ?? 0),
        target.pageIds.length
      );
      insertPage(ws, cmd.pageId, target.scriptId, at, cmd);
    }
  },

  [CommandType.SET_QUESTION_START]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    if (!script.pageIds.includes(cmd.pageId)) reject('PAGE_NOT_IN_SCRIPT', cmd);
    if (!Number.isInteger(cmd.lineIndex) || cmd.lineIndex < 0) {
      reject('LINE_OUT_OF_RANGE', cmd);
    }
    const question = (ws.questions || []).find(
      (entry) => entry.questionId === cmd.questionId
    );
    if (ws.questions?.length && !question) reject('QUESTION_NOT_FOUND', cmd);
    const others = script.questionStarts.filter(
      (entry) => entry.questionId !== cmd.questionId
    );
    ws.scripts[cmd.scriptId] = {
      ...script,
      questionStarts: [
        ...others,
        {
          questionId: cmd.questionId,
          label: question?.label ?? null,
          pageId: cmd.pageId,
          lineIndex: cmd.lineIndex,
          source: 'MANUAL',
          found: true,
        },
      ],
    };
  },

  [CommandType.CLEAR_QUESTION_START]: (ws, cmd) => {
    const script = scriptOf(ws, cmd.scriptId, cmd);
    ws.scripts[cmd.scriptId] = {
      ...script,
      questionStarts: script.questionStarts.map((entry) =>
        entry.questionId === cmd.questionId
          ? {
              ...entry,
              pageId: null,
              lineIndex: null,
              source: 'MANUAL',
              found: false,
            }
          : entry
      ),
    };
  },

  [CommandType.ACCEPT_SUGGESTIONS]: (ws, cmd) => {
    if (!(Number(cmd.minConfidence) >= ACCEPT_MIN_CONFIDENCE)) {
      reject('CONFIDENCE_TOO_LOW', cmd);
    }
    const candidates = cmd.pageIds ? cmd.pageIds : finalize(ws).looseIds;
    candidates.forEach((pageId) => {
      const page = ws.pages[pageId];
      if (!page || page.kind !== RESPONSE || page.scriptId) return;
      const top = topSuggestion(page);
      if (!top || top.confidence < cmd.minConfidence) return;
      const target = ws.scripts[top.scriptId];
      if (!target) return;
      const at = Math.min(
        Math.max(0, top.position ?? target.pageIds.length),
        target.pageIds.length
      );
      insertPage(ws, pageId, target.scriptId, at, cmd);
    });
  },

  [CommandType.EDIT_PAGE_TEXT]: (ws, cmd) => {
    const page = pageOf(ws, cmd.pageId, cmd);
    if (page.source === 'TYPED') reject('PAGE_TYPED_USE_TYPED_EDIT', cmd);
    if (typeof cmd.text !== 'string' || cmd.text.length > MAX_PAGE_TEXT) {
      reject('TEXT_TOO_LONG', cmd);
    }
    ws.pages[cmd.pageId] = { ...page, edited: true };
    if (page.scriptId && ws.scripts[page.scriptId]) {
      ws.scripts[page.scriptId] = {
        ...ws.scripts[page.scriptId],
        transcriptionEdited: true,
      };
    }
  },

  [CommandType.REVERT_PAGE_TEXT]: (ws, cmd) => {
    const page = pageOf(ws, cmd.pageId, cmd);
    if (!page.edited) reject('NOT_EDITED', cmd);
    ws.pages[cmd.pageId] = { ...page, edited: false };
  },

  [CommandType.ROTATE_PAGE]: (ws, cmd) => {
    const page = pageOf(ws, cmd.pageId, cmd);
    if (!ROTATIONS.includes(cmd.rotation)) reject('INVALID_ROTATION', cmd);
    ws.pages[cmd.pageId] = { ...page, rotation: cmd.rotation };
  },

  [CommandType.SET_MODE]: (ws, cmd) => {
    if (!['AUTO', 'MANUAL'].includes(cmd.mode)) reject('INVALID_MODE', cmd);
    if (ws.mode === cmd.mode) reject('ALREADY_THAT_MODE', cmd);
    ws.mode = cmd.mode;
    if (cmd.mode !== 'MANUAL') return;
    Object.keys(ws.pages).forEach((id) => {
      const page = ws.pages[id];
      if (page.kind !== RESPONSE) return;
      ws.pages[id] = { ...page, scriptId: null, position: -1, suggestions: [] };
    });
    ws.scripts = {};
  },

  [CommandType.ASSIGN_PAGES_TO_STUDENT]: (ws, cmd) => {
    const ids = cmd.pageIds || [];
    if (ids.length === 0) reject('EMPTY_SCRIPT', cmd);
    const entry = rosterEntry(ws, cmd.rosterId);
    if (ws.roster?.length && !entry) reject('ROSTER_ENTRY_NOT_FOUND', cmd);
    ids.forEach((id) => requireResponse(pageOf(ws, id, cmd), cmd));
    ids.forEach((id) => detachPage(ws, id));
    let script = Object.values(ws.scripts).find(
      (candidate) => candidate.rosterId === cmd.rosterId
    );
    if (!script) {
      const scriptId = `pending-roster-${cmd.rosterId}`;
      createScript(ws, scriptId, {
        name: entry?.name ?? null,
        studentId: entry?.studentId ?? null,
        rosterId: cmd.rosterId,
      });
      ws.scripts[scriptId] = {
        ...ws.scripts[scriptId],
        matchNote: 'ASSIGNED_BY_YOU',
      };
      script = ws.scripts[scriptId];
    }
    setScriptPages(ws, script.scriptId, [
      ...ws.scripts[script.scriptId].pageIds,
      ...ids,
    ]);
  },
};

/** The most likely script for a loose page (highest confidence), or null. */
export const topSuggestion = (page) => {
  const list = Array.isArray(page?.suggestions) ? page.suggestions : [];
  return list.reduce(
    (best, entry) =>
      !best || (entry?.confidence ?? 0) > (best.confidence ?? 0) ? entry : best,
    null
  );
};

/**
 * Apply one command to a workspace. Returns {ws, prev}; throws
 * CommandRejected (reason = 01 §6.2 validation code) for impossible input.
 */
export const applyCommand = (ws, command) => {
  const apply = APPLY[command?.type];
  if (!apply) throw new CommandRejected('UNKNOWN_COMMAND', command);
  const next = draft(ws);
  apply(next, command);
  return { ws: finalize(next), prev: ws };
};

/** Apply a group of commands in order (all-or-nothing, like the server). */
export const applyCommands = (ws, commands) =>
  (commands || []).reduce(
    (current, command) => applyCommand(current, command).ws,
    ws
  );
